<script>
    import { Chart, registerables } from 'chart.js';
    import { Tooltip } from 'sveltestrap';
    import Spinner from 'svelte-spinner';

    import { formatDate, getApiDateFormat, isToday, getIsoDateStringAsDate } from '../../date-time.js';
    import { vptServer, token, reportDate, selectedAspect, inmateCount } from '../../stores.js';
    import { config, countActiveIsolationElements } from "../../config";
    import { createRequestParams } from "../../string.js";
    
    export let requestProperties;

    let chart;
    Chart.register(...registerables);

    let overviewData;
    let preventAutorender = true;

    let reportDateDescription;
    $: { if ($reportDate != undefined) { 
            reportDateDescription = "on " + formatDate(getApiDateFormat($reportDate));
        }
    }

    $: {
        if (requestProperties && requestProperties.requestDate) {
            getIsolationOverview(requestProperties.requestDate + createRequestParams(requestProperties.requestParams));
        }
    }

    async function getIsolationOverview(requestString) {
        if (!preventAutorender) {
            try {
                chart.destroy();
                overviewData = null;
            }
            catch {
            }
            preventAutorender = true;
        }

        await fetch(`${$vptServer}/establishment/isolation/overview/${requestString}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
            .then(r => {
                if (!r.ok) {
                    throw Error(r.status);
                }
                return r;
            })
            .then(r => r.json())
            .then(data => {
                overviewData = data;
            })
            .catch(error => {
                console.error("Error getting establishment isolation overview");
                console.error(error);
                return;
            });

        renderIsolationChart();

        // Allow filter changes to update the graph
        preventAutorender = false;
    }

    function renderIsolationChart() {
        if (overviewData && overviewData.countTotalInmates > 0) {
            var ctx = document.getElementById("isolation-chart-canvas").getContext("2d");
            chart = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Targeted', 'Not Targeted'],
                    datasets: [{
                        label: 'Isolation Summary',
                        data: [ {count: overviewData.countTargetedInmates, percentage: overviewData.percentageTargetedInmates}, 
                                {count: (overviewData.countTotalInmates - overviewData.countTargetedInmates), percentage: (100 - overviewData.percentageTargetedInmates)}
                            ],
                        backgroundColor: ['#3e1d60', '#d4d5d6'],
                    }]
                },
                options: {
                    cutout: '65%',
                    maintainAspectRatio: false,
                    parsing: { 
                        key: 'count'
                    },
                    plugins: {
                        legend: {display: false},
                        tooltip: {
                            displayColors: false,
                            callbacks: {
                                label: function(context) {
                                    return context.label + ": " + context.raw.percentage + "%";
                                }
                            }
                        }
                    }
                }
            });
        }
    }

    $: numberOfActiveElements = countActiveIsolationElements($config);
</script>

<style>
    .establishment-widget {        
        width: 330px;
    }

    .establishment-widget.chart-only {
        width: 126px;
    }
    
    .breakdown-widget .table td {
        padding: 0;
    }

    .badge {
        line-height: 5px;
        width: 16px;
        height: 10px;
        font-size: 12px;
        color: #dc3545;
    }

    .badge.long-badge {
        width: 21px;
        color: white;
    }
</style>

<div class="establishment-widget" class:chart-only={numberOfActiveElements < 2}>
    {#if overviewData && overviewData.countTotalInmates > 0 && !($inmateCount.filtered > $inmateCount.unfiltered)}
        <button on:click class="button-reset focus-outline card" class:selected={$selectedAspect === "Isolation"}>
            <div class="card-body">
                <div class="widget-container">
                    <span class="widget-label">Isolation</span>

                    <div class="widget-content widget-pad-right">
                        <div class="chart-widget">
                            <canvas id="isolation-chart-canvas"></canvas>

                            <span id="count-indicator-isolation" class="chart-count"
                                  class:trend-up={overviewData.trendIndicatorTargetedInmates == 'U'}
                                  class:trend-down={overviewData.trendIndicatorTargetedInmates == 'D'}>
                                        {overviewData.countTargetedInmates}
                                </span>
                            <Tooltip target="count-indicator-isolation" placement="bottom">
                                {overviewData.countTargetedInmates} of {overviewData.countTotalInmates} ({overviewData.percentageTargetedInmates}%) {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'are' : 'were'} 
                                targeted {isToday($reportDate) ? 'today' : reportDateDescription}
                            </Tooltip>
                            
                            {#if overviewData.trendIndicatorTargetedInmates == 'U'}
                                    <span id="trend-indicator-isolation-u" class="trend-indicator trend-up">
                                        <img src="/assets/arrow/arrow-up-danger.svg" alt="Increasing Trend" />
                                    </span>
                                <Tooltip target="trend-indicator-isolation-u" placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargetedInmates}% were targeted</Tooltip>
                            {:else if overviewData.trendIndicatorTargetedInmates == 'D'}
                                    <span id="trend-indicator-isolation-d" class="trend-indicator trend-down">
                                        <img src="/assets/arrow/arrow-down-success.svg" alt="Decreasing Trend" />
                                    </span>
                                <Tooltip target="trend-indicator-isolation-d" placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargetedInmates}% were targeted</Tooltip>
                            {/if}
                        </div>

                        {#if numberOfActiveElements > 1}
                        <div class="breakdown-widget">
                            <table class="table table-sm table-borderless">
                                <tbody>
                                {#if numberOfActiveElements >= 4}    
                                    <tr>
                                        <td>
                                            <span class="badge badge-danger">-</span>
                                            <span class="badge badge-danger">-</span>
                                            <span class="badge badge-danger">-</span>
                                            <span class="badge long-badge badge-danger">+</span>
                                        </td>
                                        <td id="legend-isolation-count-over-3">
                                            {overviewData.countHighLevelsOver3}
                                            <Tooltip target="legend-isolation-count-over-3" placement="bottom">{overviewData.countHighLevelsOver3} of {overviewData.countTotalInmates} {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'have' : 'had'} over 3 High levels {isToday($reportDate) ? 'today' : reportDateDescription}</Tooltip>
                                        </td>
                                        <td class="card-legend-percentage" id="legend-isolation-percentage-over-3">
                                            ({overviewData.percentageHighLevelsOver3}%)
                                        </td>
                                        <td class="card-legend-trend" id="legend-isolation-trend-over-3">
                                            {#if overviewData.trendIndicatorHighLevelsOver3 == 'U'}
                                                <span class="trend-icon text-danger"><i class="bi bi-arrow-up-short" /></span>
                                                <Tooltip target="legend-isolation-trend-over-3" placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageHighLevelsOver3}% had over 3 High levels</Tooltip>
                                            {:else if overviewData.trendIndicatorHighLevelsOver3 == 'D'}
                                                <span class="trend-icon text-success"><i class="bi bi-arrow-down-short" /></span>
                                                <Tooltip target="legend-isolation-trend-over-3" placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageHighLevelsOver3}% had over 3 High levels</Tooltip>
                                            {:else}
                                                <span class="text-disabled trend-icon"><i class="bi bi-dash" /></span>
                                            {/if}
                                        </td>
                                    </tr>
                                {/if}
                                
                                {#if numberOfActiveElements >= 3} 
                                    <tr>
                                        <td>
                                            <span class="badge badge-danger">-</span>
                                            <span class="badge badge-danger">-</span>
                                            <span class="badge badge-danger">-</span>
                                        </td>
                                        <td id="legend-isolation-count-3">
                                            {overviewData.countHighLevels3}
                                            <Tooltip target="legend-isolation-count-3" placement="bottom">{overviewData.countHighLevels3} of {overviewData.countTotalInmates} {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'have' : 'had'} 3 High levels {isToday($reportDate) ? 'today' : reportDateDescription}</Tooltip>
                                        </td>
                                        <td class="card-legend-percentage" id="legend-isolation-percentage-3">
                                            ({overviewData.percentageHighLevels3}%)
                                        </td>
                                        <td class="card-legend-trend" id="legend-isolation-trend-3">
                                            {#if overviewData.trendIndicatorHighLevels3 == 'U'}
                                                <span class="trend-icon"><i class="bi bi-arrow-up-short" /></span>
                                                <Tooltip target="legend-isolation-trend-3" placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageHighLevels3}% had 3 High levels</Tooltip>
                                            {:else if overviewData.trendIndicatorHighLevels3 == 'D'}
                                                <span class="trend-icon"><i class="bi bi-arrow-down-short" /></span>
                                                <Tooltip target="legend-isolation-trend-3" placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageHighLevels3}% had 3 High levels</Tooltip>
                                            {:else}
                                                <span class="text-disabled trend-icon"><i class="bi bi-dash" /></span>
                                            {/if}
                                        </td>
                                    </tr>
                                {/if}

                                {#if numberOfActiveElements >= 2} 
                                    <tr>
                                        <td>
                                            <span class="badge badge-danger">-</span>
                                            <span class="badge badge-danger">-</span>
                                        </td>
                                        <td id="legend-isolation-count-2">
                                            {overviewData.countHighLevels2}
                                            <Tooltip target="legend-isolation-count-2" placement="bottom">{overviewData.countHighLevels2} of {overviewData.countTotalInmates} {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'have' : 'had'} 2 High levels {isToday($reportDate) ? 'today' : reportDateDescription}</Tooltip>
                                        </td>
                                        <td class="card-legend-percentage" id="legend-isolation-percentage-2">
                                            ({overviewData.percentageHighLevels2}%)
                                        </td>
                                        <td class="card-legend-trend" id="legend-isolation-trend-2">
                                            {#if overviewData.trendIndicatorHighLevels2 == 'U'}
                                                <span class="trend-icon"><i class="bi bi-arrow-up-short" /></span>
                                                <Tooltip target="legend-isolation-trend-2" placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageHighLevels2}% had 2 High levels</Tooltip>
                                            {:else if overviewData.trendIndicatorHighLevels2 == 'D'}
                                                <span class="trend-icon"><i class="bi bi-arrow-down-short" /></span>
                                                <Tooltip target="legend-isolation-trend-2" placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageHighLevels2}% had 2 High levels</Tooltip>
                                            {:else}
                                                <span class="text-disabled trend-icon"><i class="bi bi-dash" /></span>
                                            {/if}
                                        </td>
                                    </tr>
                                {/if}

                                <tr>
                                    <td><span class="badge badge-danger">-</span></td>
                                    <td id="legend-isolation-count-1">
                                        {overviewData.countHighLevels1}
                                        <Tooltip target="legend-isolation-count-1" placement="bottom">{overviewData.countHighLevels1} of {overviewData.countTotalInmates} {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'have' : 'had'} 1 High level {isToday($reportDate) ? 'today' : reportDateDescription}</Tooltip>
                                    </td>
                                    <td class="card-legend-percentage" id="legend-isolation-percentage-1">
                                        ({overviewData.percentageHighLevels1}%)
                                    </td>
                                    <td class="card-legend-trend" id="legend-isolation-trend-1">
                                        {#if overviewData.trendIndicatorHighLevels1 == 'U'}
                                            <span class="trend-icon"><i class="bi bi-arrow-up-short" /></span>
                                            <Tooltip target="legend-isolation-trend-1" placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageHighLevels1}% had 1 High level</Tooltip>
                                        {:else if overviewData.trendIndicatorHighLevels1 == 'D'}
                                            <span class="trend-icon"><i class="bi bi-arrow-down-short" /></span>
                                            <Tooltip target="legend-isolation-trend-1" placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageHighLevels1}% had 1 High level</Tooltip>
                                        {:else}
                                            <span class="text-disabled trend-icon"><i class="bi bi-dash" /></span>
                                        {/if}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <span>-</span>
                                    </td>
                                    <td id="legend-isolation-count-0">
                                        {overviewData.countHighLevels0}
                                        <Tooltip target="legend-isolation-count-0" placement="bottom">{overviewData.countHighLevels0} of {overviewData.countTotalInmates} {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'have' : 'had'} no High levels {isToday($reportDate) ? 'today' : reportDateDescription}</Tooltip>
                                    </td>
                                    <td class="card-legend-percentage" id="legend-isolation-percentage-0">
                                        ({overviewData.percentageHighLevels0}%)
                                    </td>
                                    <td class="card-legend-trend" id="legend-isolation-trend-0">
                                        {#if overviewData.trendIndicatorHighLevels0 == 'U'}
                                            <span class="trend-icon text-success"><i class="bi bi-arrow-up-short" /></span>
                                            <Tooltip target="legend-isolation-trend-0" placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageHighLevels0}% had no High levels</Tooltip>
                                        {:else if overviewData.trendIndicatorHighLevels0 == 'D'}
                                            <span class="trend-icon text-danger"><i class="bi bi-arrow-down-short" /></span>
                                            <Tooltip target="legend-isolation-trend-0" placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageHighLevels0}% had no High levels</Tooltip>
                                        {:else}
                                            <span class="text-disabled trend-icon"><i class="bi bi-dash" /></span>
                                        {/if}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                        {/if}
                    </div>
                </div>
            </div>
        </button>
    {:else if overviewData}    
        <div class="card" class:selected={$selectedAspect === "Isolation"}>
            <div class="card-body">
                <div class="widget-container">
                    <span class="widget-label">Isolation</span>

                    <div class="widget-content">
                        {#if ($inmateCount.filtered > $inmateCount.unfiltered) || ($inmateCount.unfiltered == 0 && isToday(getIsoDateStringAsDate(requestProperties.requestDate)))}
                            <span class="no-records">Data is being regenerated.</span>
                        {:else}
                            <span class="no-records">No Isolation data is available for {isToday($reportDate) ? 'today' : formatDate(getApiDateFormat($reportDate))}.</span>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    {:else}
        <div class="card" class:selected={$selectedAspect === "Isolation"}>
            <div class="card-body">
                <div class="widget-container">
                    <div class="widget-spinner">
                        <Spinner size="100" speed="750" color="#3d5980" thickness="3" gap="15" />
                    </div>
                </div>
            </div>
        </div>
    {/if}
</div>


